import {
    Input,
    Output,
    Component,
    EventEmitter,
    ViewChild,
    OnInit,
    ContentChild,
    TemplateRef,
    ElementRef
} from '@angular/core';
import { IconType } from '../../core-data-model';
import { OldIconConfig } from '../../core-ui/OldIcon/OldIconConfig';

// This makes the dropdown content more visibly offset and thus connected to the
// dropdown click location from any other bordered elements that abut the clickable area
const dropdownContentOffset = -1;

export enum DropdownEntryType {
    Category = 1,
    SubMenu
}

export interface DropdownListEntry {
    managesOwnClick?: boolean;
    tooltip?: string;
    title?: string;
    entryType?: DropdownEntryType;
    children?: any[];
    _open?: boolean;
    _parent?: DropdownListEntry;
}

export type TooltipOptions = {
    content: string;
    placement?: string;
    disabled?: boolean;
};

// Note that Mousedown fires BEFORE focusout which allows this to work
@Component({
    selector: 'pi-dropdown',
    host: {
        '[class.pi-dropdown]': 'true',
        '[class.dropdown--disabled]': 'isDisabled'
    },
    template: `
        <div (clickOutside)="focusLost()">
            <div
                #clickArea
                (click)="showAllValues()"
                class="pi-dropdown_clickPrompt"
                tooltip
                [tooltipContent]="dropdownTooltip.content"
                [tooltipDisabled]="dropdownTooltip.disabled"
                [tooltipPlacement]="dropdownTooltip.placement"
            >
                @if (placeholderIcon) {
                    <i
                        class="pi-dropdown_placeholderIcon fa"
                        [ngClass]="placeholderIcon"
                    >
                    </i>
                }
                @if (svgContent) {
                    <old-icon [properties]="svgContent"></old-icon>
                }
                {{ shownValue }}
                <abstract-icon
                    class="pi-dropdown_caretIcon"
                    [iconType]="iconType"
                ></abstract-icon>
            </div>
            <ng-container
                *ngTemplateOutlet="
                    menuList;
                    context: {
                        list: this.list,
                        open: this.showDropdown(),
                        width: this.getWidth(),
                        top: this.getTop(),
                        left: 'auto',
                        right: this.getRight(),
                        bottom: this.getBottom()
                    }
                "
            >
            </ng-container>
        </div>

        <ng-template
            #menuList
            let-list="list"
            let-open="open"
            let-top="top"
            let-width="width"
            let-left="left"
            let-right="right"
            let-bottom="bottom"
        >
            @if (open) {
                <ul
                    class="dropdownContent"
                    [ngStyle]="{
                        'top.px': top,
                        'width.px': width,
                        left: left,
                        right: right,
                        'bottom.px': bottom
                    }"
                >
                    @for (entry of list | slice: 0 : 100; track entry) {
                        <ng-container
                            *ngTemplateOutlet="
                                menuEntry;
                                context: { entry: entry }
                            "
                        >
                        </ng-container>
                    }
                    <ng-content> </ng-content>
                </ul>
            }
        </ng-template>

        <ng-template #menuEntry let-entry="entry">
            @switch (entry.entryType) {
                @case (Type.Category) {
                    <li class="dropdown_category">
                        {{ entry.title }}
                    </li>
                    @for (
                        entry of entry.children | slice: 0 : 100;
                        track entry
                    ) {
                        <ng-container
                            *ngTemplateOutlet="
                                menuEntry;
                                context: { entry: entry }
                            "
                        >
                        </ng-container>
                    }
                }
                @case (Type.SubMenu) {
                    <li
                        #menuLaunch
                        class="dropdownContent_submenu flipUp"
                        (click)="openEntry(entry)"
                        (mouseover)="pickMe(entry, $event)"
                        [ngClass]="{
                            highlight: selectedItem === entry
                        }"
                    >
                        {{ entry.title }}
                        <span class="pi-dropdown_caretIcon">
                            <i class="fa fa-angle-right"></i>
                        </span>
                        <ng-container
                            *ngTemplateOutlet="
                                menuList;
                                context: {
                                    list: entry.children,
                                    open: entry._open,
                                    left: menuLaunch.offsetWidth + 'px',
                                    top: -(menuLaunch.offsetTop + 1)
                                }
                            "
                        >
                        </ng-container>
                    </li>
                }
                @default {
                    <li
                        (click)="itemClicked($event, entry)"
                        (mouseover)="pickMe(entry, $event)"
                        [ngClass]="{
                            highlight: selectedItem === entry
                        }"
                    >
                        <ng-template
                            [ngTemplateOutlet]="contentTemplate"
                            [ngTemplateOutletContext]="{
                                item: entry
                            }"
                        >
                        </ng-template>
                    </li>
                }
            }
        </ng-template>
    `,
    standalone: false
})
export class DropdownComponent implements OnInit {
    Type = DropdownEntryType;

    @ContentChild(TemplateRef, { static: false })
    contentTemplate: TemplateRef<any>;
    @ViewChild('clickArea', { static: true })
    clickArea: ElementRef;

    @Input()
    placeholder: string;
    @Input()
    placeholderIcon: string;
    @Input()
    svgContent: OldIconConfig;
    @Input()
    list: DropdownListEntry[];
    @Input()
    isDisabled = false;
    @Input()
    direction?: {
        horizontal?: 'left' | 'center' | 'right';
        vertical?: 'top' | 'center' | 'bottom';
    };
    @Input()
    dropdownTooltip: TooltipOptions = {
        content: '',
        disabled: true
    };

    // Events that are emitted
    @Output()
    readonly onSelected = new EventEmitter<any>();
    @Output()
    readonly onDropDownOpened = new EventEmitter<any>();

    selectedItem: DropdownListEntry = null;

    shownValue: string;
    hasFocus = false;

    textFieldChanged = false;

    iconType = IconType.ChevronDown;

    ngOnInit() {
        this.shownValue = this.placeholder;
    }

    pickMe(entry: DropdownListEntry, event: MouseEvent) {
        event.stopPropagation();
        this.selectedItem = entry;
    }

    openEntry(entry: DropdownListEntry) {
        // fill in parent info as needed
        if (entry.children) {
            entry.children.forEach((e) => {
                e._parent = entry;
            });
        }
        this.closeSubMenu(this.list);
        this.recursiveOpen(entry);
    }

    private recursiveOpen(entry: DropdownListEntry) {
        entry._open = true;
        if (entry._parent) {
            this.recursiveOpen(entry._parent);
        }
    }

    showDropdown(): boolean {
        return this.hasFocus;
    }

    showAllValues() {
        if (this.isDisabled) {
            return;
        }
        this.onDropDownOpened.emit(false);
        this.hasFocus = !this.hasFocus;
    }

    focusLost() {
        this.hasFocus = false;
        this.closeSubMenu(this.list);
    }

    private closeSubMenu(entries: DropdownListEntry[]) {
        if (entries) {
            entries.forEach((entry) => {
                entry._open = false;
                if (entry.children) {
                    this.closeSubMenu(entry.children);
                }
            });
        }
    }

    getTop() {
        if (this.direction && this.direction.vertical) {
            return;
        }
        const positionInfo =
            this.clickArea.nativeElement.getBoundingClientRect();
        // This breaks dropdown on create job page among others
        // return positionInfo.top + positionInfo.height;
        return positionInfo.height + dropdownContentOffset;
    }
    getRight() {
        if (!this.direction) {
            return 'auto';
        }

        return this.direction.horizontal === 'left' ? '0' : 'auto';
    }
    getBottom() {
        if (!this.direction) {
            return 'auto';
        }
        const positionInfo =
            this.clickArea.nativeElement.getBoundingClientRect();
        return this.direction.vertical === 'top'
            ? positionInfo.height + 2
            : 'auto';
    }
    getWidth() {
        return this.clickArea.nativeElement.offsetWidth;
    }

    itemClicked(event: Event, item: DropdownListEntry): void {
        event.preventDefault();
        if (!item.managesOwnClick) {
            this.onDropDownOpened.emit(true);
            this.onSelected.emit(item);
            this.focusLost();
        }
    }
}
